import { useState } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { joinAppWaitlist } from "../../lib/leads";

/**
 * "App Coming Soon" modal, opened from ChartModal's "App laden & komplettes
 * Chart erhalten" button (there is no app yet — see that button's onClick
 * in ChartModal.tsx). Collects an email for the app waitlist instead of
 * pretending a download link exists.
 */
export function AppWaitlistModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await joinAppWaitlist({ email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Eintragung fehlgeschlagen. Bitte versuch es erneut.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/40 p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      {/* data-lenis-prevent — see ChartModal.tsx's identical comment for
          why this is required on every fixed-overlay modal in a
          Lenis-driven page (hooks/useLenis.ts). */}
      <div data-lenis-prevent className="glass relative w-full max-w-sm p-8" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="absolute right-5 top-5 text-xl leading-none text-charcoal/40 hover:text-charcoal"
        >
          ✕
        </button>

        {sent ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="text-3xl text-gold">✦</span>
            <p className="font-display text-lg">Du stehst auf der Liste!</p>
            <p className="text-sm text-charcoal/60">
              Wir schreiben dir, sobald die jyotishASTRO-App verfügbar ist.
            </p>
            <Button className="mt-2" onClick={onClose}>Schließen</Button>
          </div>
        ) : (
          <>
            <p className="eyebrow mb-2 text-center">App Coming Soon ✦</p>
            <h3 className="mb-2 text-center text-xl font-semibold">
              Die jyotishASTRO-App ist noch in Arbeit
            </h3>
            <p className="mb-6 text-center text-sm text-charcoal/60">
              Trag dich in die Warteliste ein — wir informieren dich als Erstes, sobald du dein
              komplettes Chart direkt in der App erhalten kannst.
            </p>
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <Input
                id="waitlist-email"
                type="email"
                label="E-Mail"
                placeholder="name@beispiel.de"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Trage ein …" : "Zur Warteliste hinzufügen"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
